import {
  setOrder,
  getOrder,
  setIndex,
  getIndex,
  setProducts,
  getProducts,
  setPrograms,
  getPrograms
} from '@/store/utils/order';
import { getMemberId } from '@/store/utils/member';
import orderApi from '@/api/order';
import { fetchProduct } from '@/api/product';
import { concat, pick, get, cloneDeep } from 'lodash';

let order = getOrder();

let index = getIndex() ?? 1;

let products = getProducts();

let programs = getPrograms();

let coupons = [];
//小計再折抵類型 1:折扣 2:折價 null不使用
let subtotalDiscountType = '0';
let subtotalDiscountNumber = 0;

// 訂單折扣 api 回傳的預設值，calcOrderPrice (order/calc-order-price) 必須先傳訂單折扣，如果還沒有訂單折扣就先傳預設值
// 詳見 calcOrderPrice
const DISCOUNT_DEFAULT = {
  id: null,
  type: null,
  name: null,
  title: null,
  price: 0,
  freebie_limit: 0
};

const state = {
  order,
  index,
  products,
  programs,
  coupons,
  subtotalDiscountType,
  subtotalDiscountNumber,
  discount: null,
  discountItems: [],
  additionalPrice: [],
  freebies: [],
  // 訂單折後前的小計
  subtotalBeforeOrderDiscount: 0
};

const actions = {
  addProduct({ commit, dispatch }, { product, push = true }) {
    let storageProducts = getProducts();

    commit('setIndex', index++);

    if (push) {
      storageProducts.push(product);
    } else {
      for (let i = 0; i < storageProducts.length; i++) {
        if (storageProducts[i].id == product.id) {
          storageProducts[i] = product;

          break;
        }
      }
    }

    commit('setProducts', storageProducts);

    dispatch('calcOrderPrice');
  },
  updateProduct({ commit, dispatch }, { product, productIndex }) {
    let storageProducts = getProducts();

    storageProducts[productIndex] = product;

    commit('setProducts', storageProducts);

    dispatch('calcOrderPrice');
  },
  addProgram({ commit, dispatch }, { program, push = false }) {
    let storagePrograms = getPrograms();
    let itemIndex = storagePrograms.findIndex(element => {
      return element.program_id == program.program_id;
    });

    if (push || itemIndex === -1) {
      storagePrograms.push(program);
    } else {
      storagePrograms[itemIndex] = program;
    }

    commit('setPrograms', storagePrograms);

    dispatch('calcOrderPrice');
  },
  updateProgram({ commit, dispatch }, { program, programIndex }) {
    let storagePrograms = getPrograms();

    storagePrograms[programIndex] = program;

    commit('setPrograms', storagePrograms);

    dispatch('calcOrderPrice');
  },
  removeProduct({ commit, dispatch }, { productId, productIndex }) {
    let storageProducts = getProducts();

    // storageProducts.splice(productIndex, 1);
    storageProducts = storageProducts.filter((storageProduct, index) => {
      return !(
        storageProduct.product_id === productId && index === productIndex
      );
    });

    commit('setProducts', storageProducts);

    dispatch('calcOrderPrice');
  },
  // 專為加價購與贈品 modal 設計
  // 傳入參數：
  // type: String, 類型是加價購(additionalPrice)或者贈品(freebies)
  // addProducts: Array, 要添加的產品
  // removeProductIds: Array, 要去除的產品規格 id
  updateOrderDiscount(
    { commit, dispatch, state },
    { type, addProducts, removeProductIds }
  ) {
    let productContainer = [];

    // 去除加價購商品, 過濾掉 removeProductIds 中存在的 id, 並存入 productContainer
    productContainer = cloneDeep(
      state[type].filter(
        product =>
          !removeProductIds.includes(product.product_spec.product_spec_id)
      )
    );

    // 添加加價購商品，因為要辨別商品規格，所以改使用 product_spec_id (規格id)
    for (let i = 0; i < addProducts.length; i++) {
      // 遍歷 addProducts 中的商品有沒有已經在 productContainer 中的
      const idx = productContainer.findIndex(
        product =>
          product.product_spec.product_spec_id ===
          addProducts[i].product_spec.product_spec_id
      );
      if (idx < 0) {
        // 如果 addProducts 當前遍歷的商品沒有在 productContainer 中，就 push
        productContainer.push(addProducts[i]);
      } else {
        // 如果 addProducts 當前遍歷的商品有在 productContainer 中，就把該商品的數量加上去
        for (let j = 0; j < productContainer.length; j++) {
          if (
            productContainer[j].product_spec.product_spec_id ===
            addProducts[i].product_spec.product_spec_id
          ) {
            productContainer[j].count += addProducts[i].count;

            break;
          }
        }
      }
    }

    commit('updateOrderDiscount', { type, data: productContainer });

    dispatch('calcOrderPrice');
  },
  removeProgram({ commit, dispatch }, programIndex) {
    let storagePrograms = getPrograms();

    storagePrograms.splice(programIndex, 1);

    commit('setPrograms', storagePrograms);

    dispatch('calcOrderPrice');
  },
  // 一般商品跟加價購商品都要經過這個 action 去計算 subtotal
  calcOrderPrice({ commit, state, getters, dispatch }, needGetDiscount = true) {
    if (getters.noItemsInCart === true) {
      dispatch('setDiscountToDefault');
    }

    let memberId = getMemberId();

    orderApi.calcOrderPrice(
      {
        programs: state.programs,
        products: state.products,
        coupons: state.coupons,
        subtotal_discount_type: state.subtotalDiscountType,
        subtotal_discount_number: getters.subtotalDiscountNum,
        uniqueId: Date.now(),
        additionalPrice: state.additionalPrice,
        discount: state.discount || DISCOUNT_DEFAULT,
        member_id: memberId
      },
      order => {
        // 先把沒有訂單折扣的小計存起來，不然下次有訂單折扣的時候，會連同訂單折扣一併計算
        if (order.discount_event_price === 0) {
          commit('setSubtotalBeforeOrderDiscount', order.subtotal);
        }

        commit('setOrder', order);

        commit('setPrograms', order.programs);

        commit('setProducts', order.products);

        commit('resetDiscountItems');

        if (order.additional_price.length > 0) {
          commit('updateOrderDiscount', {
            type: 'additionalPrice',
            data: order.additional_price
          });
        }

        if (needGetDiscount) {
          dispatch('getDiscount');
        }
      }
    );
  },
  setDiscountToDefault({ commit }) {
    commit('setSubtotalDiscountType', subtotalDiscountType); //default
    commit('setSubtotalDiscountNumber', subtotalDiscountNumber); //default
  },
  clearOrder({ commit, dispatch }) {
    commit('setOrder', null);
    commit('setIndex', 1);
    commit('setProducts', []);
    commit('setPrograms', []);
    commit('setCoupons', []);
    dispatch('setDiscountToDefault');
  },
  getDiscount({ commit, state, rootState, dispatch }) {
    let memberId = getMemberId();

    // 回傳格式
    // id: 折扣活動ID
    // type: 折扣活動類型:8=固定折扣% 9=固定折扣金額 10=贈品 11=加價購
    // name: 折扣活動名稱
    // title: 折扣活動金額名義(ex:   加購價、贈品、滿 Ｏ件，Ｏ折等等)
    // price: 若type 為8 or 9 則該值為折扣金額，其餘類型為0
    // freebie_limit: 若type 為10，可選擇贈品數量、其餘狀況為0
    orderApi.getDiscount(
      {
        branch_id: rootState.member.branchId,
        products: state.products,
        uniqueId: Date.now(),
        member_id: memberId
      },
      res => {
        // 1. 如果 res.type 回傳 null，代表沒有訂單折扣
        // 2. 如果 res.type 有回傳訂單折扣，且目前 state 中也有存訂單折扣，就看兩個訂單折扣一不一樣，不一樣
        // 就先清空訂單折扣跟訂單折扣商品
        if (
          !res.type ||
          (res.type && state.discount && res.type !== state.discount.type)
        ) {
          commit('setDiscountItems', []);
          commit('resetOrderDiscount');
        }

        if (res.type && state.discountItems.length === 0) {
          let discount_name = null;
          let discount_en_name = null;
          switch (res.type) {
            case 11:
              discount_name = '加價購';
              discount_en_name = 'additionalPrice';
              break;
            case 10:
              discount_name = '贈品';
              discount_en_name = 'freebies';
              break;
            case 9:
              discount_name = '固定折扣金額';
              dispatch('calcOrderPrice', false);
              break;
            case 8:
              discount_name = '固定折扣%';
              dispatch('calcOrderPrice', false);
              break;
            default:
              break;
          }
          dispatch('getFreebieOrAdditionalPrice', {
            id: res.id,
            discount_name,
            discount_en_name
          });
        }
        commit('setDiscount', res);
      }
    );
  },
  getFreebieOrAdditionalPrice(
    { dispatch },
    { id, discount_name, discount_en_name }
  ) {
    orderApi.getFreebieOrAdditionalPrice(id, res => {
      const discountItems = res.map(res => {
        if (res.price === null) res.price = 0;

        return {
          ...res,
          count: 0,
          model_id: -1,
          discount_type: '0',
          product_spec: {},
          discount_name,
          discount_en_name
        };
      });

      dispatch('getProductSpec', discountItems);
    });
  },
  getProductSpec({ rootState, commit }, discountItems) {
    const discountItemsWithSpec = [];
    discountItems.forEach(item => {
      fetchProduct(
        {
          branch_id: rootState.member.branchId,
          member_id: rootState.member.memberId,
          id: item.product_id
        },
        res => {
          item = {
            ...item,
            spec_categories: res.spec_categories,
            product_specs: res.product_specs
          };
          discountItemsWithSpec.push(item);

          if (discountItemsWithSpec.length === discountItems.length) {
            commit('setDiscountItems', discountItemsWithSpec);
          }
        }
      );
    });
  }
};

const mutations = {
  setOrder(state, order) {
    state.order = order;

    setOrder(order);
  },
  setIndex(state, index) {
    state.index = index;

    setIndex(index);
  },
  setProducts(state, products) {
    state.products = products;

    setProducts(products);
  },
  setPrograms(state, programs) {
    state.programs = programs;

    setPrograms(programs);
  },
  setCoupons(state, coupons) {
    state.coupons = coupons;
  },
  setSubtotalDiscountType(state, type) {
    state.subtotalDiscountType = type;
  },
  setSubtotalDiscountNumber(state, num) {
    state.subtotalDiscountNumber = num;
  },
  setDiscount(state, data) {
    state.discount = data;
  },
  setDiscountItems(state, newDiscountItems) {
    state.discountItems = newDiscountItems.sort(
      (a, b) => a.product_id - b.product_id
    );
  },
  updateDiscountItemCount(state, { product_id, count }) {
    const idx = state.discountItems.findIndex(
      item => item.product_id === product_id
    );
    state.discountItems[idx].count = count;
  },
  resetDiscountItems() {
    state.discountItems.forEach(item => {
      item.count = 0;
      item.model_id = -1;
    });
  },
  updateDiscountItemSpec(state, { product_id, chooseSpec }) {
    const discountItemsIdx = state.discountItems.findIndex(
      item => item.product_id === product_id
    );

    state.discountItems[discountItemsIdx].model_id = chooseSpec.product_spec_id;
    state.discountItems[discountItemsIdx].product_spec = chooseSpec;
  },
  updateOrderDiscount(state, { type, data }) {
    // 加入後端要的預設值
    data.forEach(d => {
      d.discount = '0';
      d.discount_number = 0;
      if (!d.subtotal) {
        d.subtotal = 0;
      }
    });

    state[type] = data;
  },
  resetOrderDiscount(state) {
    state.additionalPrice = [];
    state.freebies = [];
  },
  setSubtotalBeforeOrderDiscount(state, subtotalBeforeOrderDiscount) {
    state.subtotalBeforeOrderDiscount = subtotalBeforeOrderDiscount;
  }
};

const getters = {
  order(state) {
    return state.order;
  },

  //根據折扣類型轉換
  subtotalDiscountNum(state) {
    //打折後端api算反了
    //這邊反著處理
    return state.subtotalDiscountType == 1
      ? 100 - state.subtotalDiscountNumber
      : state.subtotalDiscountNumber;
  },

  index(state) {
    return state.index;
  },

  products(state) {
    return state.products;
  },

  programs(state) {
    return state.programs;
  },

  //cart items combined products and programs
  items(state) {
    let pickKeys = [
      'photo',
      'name',
      'subtotal',
      'count',
      'price',
      'discount_type',
      'discount_name',
      'discount_en_name',
      'product_id',
      'product_spec'
    ];
    let products = state.products.map((element, index) => {
      let product = pick(element, pickKeys);

      let specName = spec => {
        if (spec.first_spec_name == null) {
          return '';
        } else if (spec.second_spec_name == null) {
          return `(${spec.first_spec_name})`;
        } else {
          return `(${spec.first_spec_name}/${spec.second_spec_name})`;
        }
      };

      return {
        ...product,
        id: element.id,
        index: index,
        type: 'product',
        combination: get(element, 'combination.name'),
        name: `${product.name} ${specName(element.product_spec)}`
      };
    });

    let additionalPrices = state.additionalPrice.map((element, index) => {
      let additionalPrice = pick(element, pickKeys);

      let specName = spec => {
        if (spec.first_spec_name == null) {
          return '';
        } else if (spec.second_spec_name == null) {
          return `(${spec.first_spec_name})`;
        } else {
          return `(${spec.first_spec_name}/${spec.second_spec_name})`;
        }
      };

      return {
        ...additionalPrice,
        id: element.id,
        index: index,
        type: 'product',
        combination: get(element, 'combination.name'),
        name: `${additionalPrice.name} ${specName(element.product_spec)}`
      };
    });

    let freebies = state.freebies.map((element, index) => {
      let freeby = pick(element, pickKeys);

      let specName = spec => {
        if (spec.first_spec_name == null) {
          return '';
        } else if (spec.second_spec_name == null) {
          return `(${spec.first_spec_name})`;
        } else {
          return `(${spec.first_spec_name}/${spec.second_spec_name})`;
        }
      };

      return {
        ...freeby,
        id: element.id,
        index: index,
        type: 'product',
        combination: get(element, 'combination.name'),
        name: `${freeby.name} ${specName(element.product_spec)}`
      };
    });

    let programs = state.programs.map((element, index) => {
      let program = pick(element, pickKeys);

      return {
        ...program,
        id: element.program_id,
        index: index,
        combination: null,
        type: 'program'
      };
    });

    return concat(programs, products, additionalPrices, freebies);
  },

  noItemsInCart(state, getters) {
    return getters.items.length === 0;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
